import type { SpecError, SpecWarning } from "../../libs/spec-to-target-converter";

/**
 * lib (spec-to-target-converter) の warning / error code を日本語メッセージに変換する。
 *
 * lib 側の `w.message` は英語のため UI 表示には使わず、本ファイルの mapping で
 * code + context から日本語メッセージを再構成して表示する。
 *
 * context 構造は target-builder / detail-sheet-parser の emit 箇所と同期している必要あり。
 * 同期が崩れたら fallback で英語の `w.message` をそのまま返す。
 */

/** SpecWarning を日本語 1 行メッセージに変換する。 */
export function warningToJapanese(w: SpecWarning): string {
  switch (w.code) {
    case "missing_spec_id": {
      const ctx = w.context as { type?: string; name?: string } | undefined;
      const { columnLabel, typeLabel } = specIdLabels(ctx?.type);
      return `${columnLabel} が空欄のため、${typeLabel} "${ctx?.name ?? ""}" をスキップしました`;
    }
    case "duplicate_spec_id": {
      const ctx = w.context as { type?: string; specId?: string; name?: string } | undefined;
      const { columnLabel, typeLabel } = specIdLabels(ctx?.type);
      return `${columnLabel}="${ctx?.specId ?? ""}" が重複しているため、後続行 (${typeLabel} "${ctx?.name ?? ""}") をスキップしました (先に出現した行を保持)`;
    }
    case "duplicate_item_name_in_sheet": {
      const ctx = w.context as { itemName?: string } | undefined;
      return `項目名 "${ctx?.itemName ?? ""}" が同一シート内で重複しているため、親項目参照では後勝ち (最後の行) を採用しました`;
    }
    case "duplicate_item_no_in_sheet": {
      const ctx = w.context as { no?: string } | undefined;
      return `項目定義No.="${ctx?.no ?? ""}" が同一シート内で重複しているため、最初の行のみ採用し後続行を破棄しました`;
    }
    case "duplicate_detail_sheet_for_context": {
      const ctx = w.context as { contextKind?: string; contextId?: string; firstSheet?: string; skippedSheet?: string } | undefined;
      const idLabel = ctx?.contextKind === "view" ? "画面ID" : "共通コンポーネントID";
      return `${idLabel}="${ctx?.contextId ?? ""}" を宣言する詳細シートが複数あるため、先に出現したシート "${ctx?.firstSheet ?? ""}" を採用し "${ctx?.skippedSheet ?? ""}" をスキップしました`;
    }
    case "unresolved_parent_item_ref": {
      const ctx = w.context as { itemName?: string; parentItemRef?: string } | undefined;
      return `項目 "${ctx?.itemName ?? ""}" の親項目 "${ctx?.parentItemRef ?? ""}" が同一シート内に見つからないため、View / Component 直下扱いにしました`;
    }
    case "unknown_service": {
      const ctx = w.context as { missingServiceSpecId?: string; viewSpecId?: string; componentSpecId?: string } | undefined;
      const childKind = ctx?.viewSpecId !== undefined ? "View" : "Component";
      return `${childKind} の親サービス="${ctx?.missingServiceSpecId ?? ""}" がサービスリストにないため、本 ${childKind} をスキップしました`;
    }
    case "unknown_screen_id": {
      const ctx = w.context as { sheetName?: string; contextId?: string } | undefined;
      return `画面_ シート "${ctx?.sheetName ?? ""}" の画面ID="${ctx?.contextId ?? ""}" が画面リストにないため、本シート配下の項目をすべてスキップしました`;
    }
    case "unknown_common_component_id": {
      const ctx = w.context as { sheetName?: string; contextId?: string } | undefined;
      return `共通_ シート "${ctx?.sheetName ?? ""}" の共通コンポーネントID="${ctx?.contextId ?? ""}" が共通コンポーネントリストにないため、本シート配下の項目をすべてスキップしました`;
    }
    case "unknown_object_type": {
      const ctx = w.context as { objectType?: string; itemName?: string } | undefined;
      return `凡例外のオブジェクト種別="${ctx?.objectType ?? ""}" のため、Item として登録しました (項目名: ${ctx?.itemName ?? ""})`;
    }
    case "unknown_transition_type": {
      const ctx = w.context as { transitionType?: string } | undefined;
      return `遷移タイプ "${ctx?.transitionType ?? ""}" が凡例にないため、同タブ遷移 (same_tab) として登録しました`;
    }
    case "unresolved_item_ref": {
      const ctx = w.context as { section?: string; refNo?: string } | undefined;
      return `${ctx?.section ?? ""} が参照する項目定義No.="${ctx?.refNo ?? ""}" が見つからないため、本セクションをスキップしました`;
    }
    case "unresolved_screen_ref": {
      const ctx = w.context as { targetScreenId?: string; targetScreenName?: string } | undefined;
      return `画面遷移セクションの 画面ID="${ctx?.targetScreenId ?? ""}" (${ctx?.targetScreenName ?? ""}) が画面リストにないため、本画面遷移をスキップしました`;
    }
    case "unresolved_component_ref": {
      const ctx = w.context as { commonComponentId?: string } | undefined;
      return `共通コンポーネントID="${ctx?.commonComponentId ?? ""}" が共通コンポーネントリストにないため、本参照をスキップしました`;
    }
    case "invalid_parent_ref": {
      const ctx = w.context as { itemName?: string; targetType?: string; parentItemRef?: string } | undefined;
      return `${ctx?.targetType ?? ""} 行 "${ctx?.itemName ?? ""}" に親項目 "${ctx?.parentItemRef ?? ""}" が指定されていますが、section / overlay は View / Component 直下固定のため親項目指定を無視しました`;
    }
    case "invalid_field_on_non_item_type": {
      const ctx = w.context as { targetType?: string; itemName?: string; field?: string } | undefined;
      const fieldLabel = ctx?.field === "transitions" ? "画面遷移" : (ctx?.field ?? "該当フィールド");
      return `${ctx?.targetType ?? ""} 行 "${ctx?.itemName ?? ""}" に ${fieldLabel} が指定されていますが、UI 未対応のため破棄しました`;
    }
    case "circular_parent_ref": {
      const ctx = w.context as { itemName?: string; parentItemRef?: string } | undefined;
      return `親項目参照が循環しているため ("${ctx?.itemName ?? ""}" → "${ctx?.parentItemRef ?? ""}")、循環メンバーの parentId 設定をスキップしました (View / Component 直下扱い)`;
    }
    default:
      return `未対応の警告です (code: ${w.code})`;
  }
}

/** SpecError を日本語 1 行メッセージに変換する。 */
export function errorToJapanese(err: SpecError): string {
  switch (err.code) {
    case "INVALID_FILE_FORMAT":
      return `入力ファイルが Excel (.xlsx) 形式として読み込めませんでした。`;
    case "MISSING_REQUIRED_SHEET":
      return `必須シート "サービスリスト" と "画面リスト" のいずれも見つかりませんでした。`;
    case "INPUT_TOO_LARGE": {
      const ctx = err.context as
        | { kind?: "file_size" | "sheet_count" | "row_count" | "cell_length"; actual?: number; limit?: number; sheetName?: string }
        | undefined;
      switch (ctx?.kind) {
        case "file_size":
          return `ファイルサイズが上限を超えています (${ctx.actual} バイト > 上限 ${ctx.limit} バイト)`;
        case "sheet_count":
          return `シート数が上限を超えています (${ctx.actual} > 上限 ${ctx.limit})`;
        case "row_count":
          return `シート "${ctx.sheetName}" の行数が上限を超えています (${ctx.actual} > 上限 ${ctx.limit})`;
        case "cell_length":
          return `シート "${ctx.sheetName}" のセル文字列長が上限を超えています (${ctx.actual} > 上限 ${ctx.limit})`;
        default:
          return `入力サイズが上限を超えています`;
      }
    }
    case "DUPLICATE_NAME_AT_SAME_LEVEL":
      return `同一階層内に同名の項目が存在するためインポートを中止しました。仕様書を修正してから再インポートしてください。`;
    case "AMBIGUOUS_PARENT_ITEM_REF": {
      const ctx = err.context as { parentItemRef?: string; sheetName?: string } | undefined;
      const ref = ctx?.parentItemRef ?? "";
      const sheet = ctx?.sheetName ?? "";
      return `親項目 "${ref}" がシート "${sheet}" 内の同名項目に複数一致するため、配置先を特定できずインポートを中止しました。シート内で項目名が一意になるよう仕様書を修正してから再インポートしてください。`;
    }
    default:
      return `未対応のエラーです (code: ${err.code})`;
  }
}

function specIdLabels(type: string | undefined): { columnLabel: string; typeLabel: string } {
  if (type === "service") return { columnLabel: "サービスID", typeLabel: "Service" };
  if (type === "view") return { columnLabel: "画面ID", typeLabel: "View" };
  if (type === "component") return { columnLabel: "共通コンポーネントID", typeLabel: "Component" };
  return { columnLabel: "ID", typeLabel: type ?? "" };
}
